import { getAssetById, unwrapAssetEnvelope } from './getAssetById';
import { assetRecordApiLookupId, assetRecordPrimaryId } from '../utils/assetRecordPrimaryId';
import {
  assetRecordHasFacilitySnapshot,
  refineAssetsForOpen24hSearch,
} from '../utils/assetOpen24hMatch';
import {
  refineAssetsForAmenityIcons,
} from '../utils/assetAmenityMatch';
import type { FacilityIconId } from '../utils/assetFacilityIcons';
import {
  isAssistantOpenWeekdayFilter,
  refineAssetsForAssistantSchedule,
  type AssistantSchedule,
} from '../assistant/assistantSchedule';
import { resolveAssetOpeningTimesForDisplay } from './assetPayloadNormalize';

const DETAIL_TTL_MS = 90_000;
const HYDRATE_CONCURRENCY = 6;

type CachedFacilities = {
  at: number;
  facilities: unknown;
  addons: unknown;
  opening_times: unknown;
};

const facilityDetailCache = new Map<string, CachedFacilities>();

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function clearOpen24hFacilityHydrationCacheForTests(): void {
  facilityDetailCache.clear();
}

function cacheKeyForAsset(asset: Record<string, unknown>): string | null {
  return assetRecordApiLookupId(asset) ?? assetRecordPrimaryId(asset);
}

function facilitiesFromDetail(detail: unknown): {
  facilities: unknown;
  addons: unknown;
  opening_times: unknown;
} {
  const unwrapped = unwrapAssetEnvelope(detail);
  if (!isPlainObject(unwrapped)) {
    return { facilities: [], addons: null, opening_times: null };
  }
  const facilities = unwrapped.facilities ?? unwrapped.facility_ids ?? unwrapped.facilityIds ?? [];
  return {
    facilities,
    addons: unwrapped.addons ?? null,
    opening_times: unwrapped.opening_times ?? unwrapped.opening_times_json ?? null,
  };
}

async function mapPool<T, R>(items: T[], limit: number, mapper: (item: T) => Promise<R>): Promise<R[]> {
  if (items.length === 0) {
    return [];
  }
  const out: R[] = new Array(items.length);
  let next = 0;
  const workerCount = Math.min(Math.max(1, limit), items.length);
  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (true) {
        const index = next;
        next += 1;
        if (index >= items.length) {
          return;
        }
        out[index] = await mapper(items[index]);
      }
    }),
  );
  return out;
}

async function loadFacilitiesForAsset(asset: Record<string, unknown>): Promise<CachedFacilities> {
  const key = cacheKeyForAsset(asset);
  if (!key) {
    return { at: Date.now(), facilities: [], addons: null, opening_times: null };
  }
  const cached = facilityDetailCache.get(key);
  if (cached && Date.now() - cached.at < DETAIL_TTL_MS) {
    return cached;
  }
  try {
    const detail = await getAssetById(key);
    const parsed = facilitiesFromDetail(detail);
    const entry: CachedFacilities = { at: Date.now(), ...parsed };
    facilityDetailCache.set(key, entry);
    return entry;
  } catch {
    const entry: CachedFacilities = {
      at: Date.now(),
      facilities: [],
      addons: null,
      opening_times: null,
    };
    facilityDetailCache.set(key, entry);
    return entry;
  }
}

function assetHasOpeningTimesSnapshot(asset: Record<string, unknown>): boolean {
  return resolveAssetOpeningTimesForDisplay(asset) != null;
}

/**
 * Search/list snapshots omit `facilities` (or carry a partial list). Load GET `/assets/:id`
 * for rows that need a full facility snapshot, without mutating the shared catalog dump.
 */
export async function hydrateAssetFacilitiesForOpen24hSearch(
  assets: Record<string, unknown>[],
  options?: { force?: boolean },
): Promise<Record<string, unknown>[]> {
  const force = Boolean(options?.force);
  const missing = force
    ? assets
    : assets.filter((asset) => !assetRecordHasFacilitySnapshot(asset));
  if (missing.length === 0) {
    return assets;
  }
  const loaded = await mapPool(missing, HYDRATE_CONCURRENCY, loadFacilitiesForAsset);
  const byKey = new Map<string, CachedFacilities>();
  missing.forEach((asset, index) => {
    const key = cacheKeyForAsset(asset);
    if (key) {
      byKey.set(key, loaded[index]);
    }
  });
  return assets.map((asset) => {
    if (!force && assetRecordHasFacilitySnapshot(asset)) {
      return asset;
    }
    const key = cacheKeyForAsset(asset);
    const extra = key ? byKey.get(key) : undefined;
    if (!extra) {
      return { ...asset, facilities: [] };
    }
    return {
      ...asset,
      facilities: extra.facilities,
      addons: extra.addons ?? asset.addons,
      ...(extra.opening_times != null ? { opening_times: extra.opening_times } : {}),
    };
  });
}

/**
 * List rows often omit opening_times. Load detail so “שפתוח בשבת” can hard-filter.
 */
export async function hydrateAssetOpeningTimesForSearch(
  assets: Record<string, unknown>[],
  options?: { force?: boolean },
): Promise<Record<string, unknown>[]> {
  const force = Boolean(options?.force);
  const missing = force
    ? assets
    : assets.filter((asset) => !assetHasOpeningTimesSnapshot(asset));
  if (missing.length === 0) {
    return assets;
  }
  const loaded = await mapPool(missing, HYDRATE_CONCURRENCY, loadFacilitiesForAsset);
  const byKey = new Map<string, CachedFacilities>();
  missing.forEach((asset, index) => {
    const key = cacheKeyForAsset(asset);
    if (key) {
      byKey.set(key, loaded[index]);
    }
  });
  return assets.map((asset) => {
    if (!force && assetHasOpeningTimesSnapshot(asset)) {
      return asset;
    }
    const key = cacheKeyForAsset(asset);
    const extra = key ? byKey.get(key) : undefined;
    if (!extra || extra.opening_times == null) {
      return asset;
    }
    return {
      ...asset,
      opening_times: extra.opening_times,
      ...(Array.isArray(extra.facilities) && extra.facilities.length > 0
        ? { facilities: extra.facilities, addons: extra.addons ?? asset.addons }
        : {}),
    };
  });
}

/** Hydrate missing facility snapshots, then keep only 24/7 Access listings. */
export async function refineOpen24hSearchWithHydration(
  assets: Record<string, unknown>[],
  known24x7Ids: ReadonlySet<number> | number[] = [],
  facilityCatalog: Record<string, unknown>[] = [],
): Promise<Record<string, unknown>[]> {
  const hydrated = await hydrateAssetFacilitiesForOpen24hSearch(assets);
  return refineAssetsForOpen24hSearch(hydrated, known24x7Ids, facilityCatalog);
}

/**
 * List snapshots often carry a partial `facilities` list. Always reload detail
 * facilities, then keep only listings that list every required amenity.
 */
export async function refineAmenitySearchWithHydration(
  assets: Record<string, unknown>[],
  requiredIcons: ReadonlyArray<FacilityIconId> | null | undefined,
  facilityCatalog: Record<string, unknown>[] = [],
): Promise<Record<string, unknown>[]> {
  const want = (requiredIcons ?? []).filter(Boolean);
  if (want.length === 0) {
    return assets;
  }
  const hydrated = await hydrateAssetFacilitiesForOpen24hSearch(assets, { force: true });
  return refineAssetsForAmenityIcons(hydrated, want, facilityCatalog);
}

/** Hydrate opening_times, then keep listings open on the requested weekday(s). */
export async function refineOpenWeekdaySearchWithHydration(
  assets: Record<string, unknown>[],
  schedule: AssistantSchedule | null | undefined,
): Promise<Record<string, unknown>[]> {
  if (!isAssistantOpenWeekdayFilter(schedule)) {
    return refineAssetsForAssistantSchedule(assets, schedule);
  }
  const hydrated = await hydrateAssetOpeningTimesForSearch(assets, { force: true });
  return refineAssetsForAssistantSchedule(hydrated, schedule);
}
